import React from "react";

/* ─── 색상 스킴별 스타일 맵 ─── */
const SOLID_ACTIVE_CLASSES = {
    // DAS-52: wallet_status_table의 FilterPill subtle 패턴(bg-brand/20 + 밝은 글자 + 옅은 보더)으로 통일.
    // 모든 테마에서 가독성·심미성이 안정적이며 사용자가 명시적으로 선호한 스타일.
    brand:   "bg-brand/20 text-brand-light border border-brand/30",
    warning: "bg-warning/20 text-warning border border-warning/30",
    gain:    "bg-gain/20 text-gain border border-gain/30",
    loss:    "bg-loss/20 text-loss border border-loss/30",
    neutral: "bg-surface-elevated text-content-secondary border border-surface-border",
};

const OUTLINE_ACTIVE_CLASSES = {
    brand:   "border border-brand bg-brand-subtle text-brand-light",
    warning: "border border-warning bg-warning-subtle text-warning",
    gain:    "border border-gain bg-gain-subtle text-gain",
    loss:    "border border-loss bg-loss-subtle text-loss",
    neutral: "border border-surface-border-light bg-surface-elevated text-content-secondary",
};

const SUBTLE_ACTIVE_CLASSES = {
    brand:   "bg-brand/20 text-brand-light border border-brand/30",
    warning: "bg-warning/20 text-warning border border-warning/30",
    gain:    "bg-gain/20 text-gain border border-gain/30",
    loss:    "bg-loss/20 text-loss border border-loss/30",
    neutral: "bg-surface-elevated text-content-secondary border border-surface-border",
};

/* ─── 사이즈별 클래스 ─── */
const SIZE_CLASSES = {
    sm: "px-3 py-1 text-xs",
    md: "px-4 py-2 text-sm",
};

/**
 * FilterPill — 필터 선택 UI용 공통 알약형 버튼 컴포넌트
 *
 * @param {string}    label        - 표시할 텍스트 (children 우선)
 * @param {boolean}   active       - 활성화 여부 (기본: false)
 * @param {string}    variant      - 'solid' | 'outline' | 'subtle' (기본: 'solid')
 * @param {string}    size         - 'sm' | 'md' (기본: 'sm')
 * @param {ReactNode} icon         - 텍스트 앞에 렌더링할 아이콘 (선택)
 * @param {string}    colorScheme  - 'brand' | 'warning' | 'gain' | 'loss' | 'neutral' (기본: 'brand')
 * @param {function}  onClick      - 클릭 핸들러
 * @param {string}    className    - 추가 Tailwind 클래스
 * @param {ReactNode} children     - label 대신 사용할 수 있는 자식 요소
 */
const FilterPill = ({
    label,
    active = false,
    variant = "solid",
    size = "sm",
    icon,
    colorScheme = "brand",
    onClick,
    className = "",
    children,
}) => {
    /* 비활성 상태 공통 클래스 */
    const inactiveClass =
        variant === "solid"
            ? "bg-surface-elevated text-content-secondary border border-surface-border hover:bg-surface-hover"
            : "border border-surface-border text-content-secondary hover:bg-surface-hover";

    /* variant + active 조합에 따른 활성 클래스 결정 */
    const getActiveClass = () => {
        if (variant === "solid")   return SOLID_ACTIVE_CLASSES[colorScheme]   ?? SOLID_ACTIVE_CLASSES.brand;
        if (variant === "outline") return OUTLINE_ACTIVE_CLASSES[colorScheme] ?? OUTLINE_ACTIVE_CLASSES.brand;
        if (variant === "subtle")  return SUBTLE_ACTIVE_CLASSES[colorScheme]  ?? SUBTLE_ACTIVE_CLASSES.brand;
        return SOLID_ACTIVE_CLASSES.brand;
    };

    /* subtle 비활성은 content-muted(=content-tertiary 대체) 사용 */
    const getInactiveClass = () => {
        if (variant === "subtle") {
            return "bg-surface-elevated text-content-muted border border-surface-border hover:bg-surface-hover hover:text-content-secondary";
        }
        return inactiveClass;
    };

    const stateClass = active ? getActiveClass() : getInactiveClass();
    const sizeClass  = SIZE_CLASSES[size] ?? SIZE_CLASSES.sm;

    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={active}
            className={[
                "inline-flex items-center rounded-full font-medium transition-colors cursor-pointer",
                sizeClass,
                stateClass,
                icon ? "gap-1.5" : "",
                className,
            ]
                .filter(Boolean)
                .join(" ")}
        >
            {/* 아이콘이 있으면 텍스트 앞에 렌더링 */}
            {icon && <span className="flex-shrink-0">{icon}</span>}
            {children ?? label}
        </button>
    );
};

export default FilterPill;
